import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, Truck, Package, Home, X, AlertTriangle, Clock, Undo2, Info } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const STEPS = [
  { key: "pending", label: "Order Placed", icon: CheckCircle2 },
  { key: "confirmed", label: "Confirmed", icon: Package },
  { key: "shipped", label: "Shipped", icon: Truck },
  { key: "delivered", label: "Delivered", icon: Home },
];

const CANCEL_REASONS = [
  { v: "changed-mind", l: "I changed my mind" },
  { v: "found-better-price", l: "Found a better price elsewhere" },
  { v: "ordered-by-mistake", l: "Ordered by mistake" },
  { v: "wrong-product", l: "Wrong product or quantity" },
  { v: "delivery-too-slow", l: "Delivery feels too slow" },
  { v: "buy-later", l: "Will buy later" },
  { v: "other", l: "Other (please tell me below)" },
];

const REASON_LABELS = {
  "changed-mind": "Customer changed their mind",
  "found-better-price": "Customer found a better price",
  "ordered-by-mistake": "Ordered by mistake",
  "wrong-product": "Wrong product or quantity",
  "delivery-too-slow": "Delivery too slow",
  "buy-later": "Customer will buy later",
  "out-of-stock": "Item out of stock",
  "address-invalid": "Address could not be verified",
  "payment-failed": "Payment failed",
  "customer-requested": "Customer requested cancellation",
  "suspected-fraud": "Suspected fraudulent order",
  "courier-unavailable": "Courier unavailable for this pincode",
  "other": "Other",
};

const RETURN_REASONS = [
  { v: "damaged-defective", l: "Item arrived damaged or defective" },
  { v: "wrong-item-received", l: "Wrong item received (different from order)" },
  { v: "doesnt-match-description", l: "Item doesn't match the description" },
  { v: "quality-not-adequate", l: "Quality is not adequate" },
  { v: "missing-parts", l: "Missing parts or accessories" },
  { v: "no-longer-needed", l: "No longer needed" },
  { v: "better-price-available", l: "Found a better price" },
  { v: "other", l: "Other (please tell us below)" },
];
const RETURN_CONDITIONS = [
  { v: "unopened-unused", l: "Unopened & unused (in original packaging)" },
  { v: "opened-unused", l: "Opened but unused" },
  { v: "used", l: "Used" },
  { v: "damaged-in-transit", l: "Damaged in transit" },
  { v: "defective", l: "Defective on arrival" },
];
const RETURN_STATUS_TIMELINE = [
  { key: "requested", label: "Requested" },
  { key: "approved", label: "Approved" },
  { key: "picked_up", label: "Picked up" },
  { key: "received", label: "Received" },
  { key: "quality_check", label: "Quality Check" },
  { key: "refund_initiated", label: "Refund Initiated" },
  { key: "refunded", label: "Refunded" },
];

const CUSTOMER_WINDOW_MIN = 60;

export default function OrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [retEligible, setRetEligible] = useState(null);
  const [retReason, setRetReason] = useState("");
  const [retNotes, setRetNotes] = useState("");
  const [retCondition, setRetCondition] = useState("");
  const [refundMethod, setRefundMethod] = useState("original");
  const [bankAccount, setBankAccount] = useState("");
  const [bankIfsc, setBankIfsc] = useState("");
  const [bankHolder, setBankHolder] = useState("");

  useEffect(() => {
    api.get(`/orders/${id}`).then((r) => setOrder(r.data));
  }, [id]);

  useEffect(() => {
    if (!order) return;
    if (order.status === "shipped" || order.status === "delivered") {
      api.get(`/orders/${order.id}/return-eligibility`).then((r) => setRetEligible(r.data)).catch(() => {});
    }
  }, [order]);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  const cancellable = useMemo(() => {
    if (!order) return { allowed: false, minutesLeft: 0 };
    if (order.status === "cancelled" || order.status === "shipped" || order.status === "delivered") return { allowed: false, minutesLeft: 0 };
    const created = new Date(order.created_at).getTime();
    const elapsedMin = (now - created) / 60000;
    const minutesLeft = Math.max(0, CUSTOMER_WINDOW_MIN - elapsedMin);
    return { allowed: minutesLeft > 0, minutesLeft: Math.floor(minutesLeft) };
  }, [order, now]);

  if (!order) return <div className="text-center py-20 font-display text-2xl">Loading…</div>;

  const stepIdx = STEPS.findIndex((s) => s.key === order.status);
  const isCancelled = order.status === "cancelled";

  const submitCancel = async () => {
    if (!reason) return toast.error("Please pick a reason");
    if (reason === "other" && !notes.trim()) return toast.error("Please tell us why so we can improve");
    setSubmitting(true);
    try {
      await api.put(`/orders/${order.id}/cancel`, { reason, notes });
      const fresh = await api.get(`/orders/${order.id}`);
      setOrder(fresh.data);
      setCancelOpen(false);
      setReason(""); setNotes("");
      toast.success("Order cancelled · confirmation email sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel");
    } finally {
      setSubmitting(false);
    }
  };

  const submitReturn = async () => {
    if (!retReason) return toast.error("Please pick a return reason");
    if (!retCondition) return toast.error("Please pick the item condition");
    if (retReason === "other" && !retNotes.trim()) return toast.error("Please describe the issue");
    if (order.payment_method === "cod" && refundMethod !== "bank_transfer") {
      return toast.error("For Cash on Delivery, please pick bank transfer and fill bank details");
    }
    if (refundMethod === "bank_transfer" && (!bankAccount || !bankIfsc || !bankHolder)) {
      return toast.error("Bank account, IFSC and holder name are required for bank transfer");
    }
    setSubmitting(true);
    try {
      await api.post(`/orders/${order.id}/return-request`, {
        reason: retReason, notes: retNotes, condition: retCondition,
        refund_method: refundMethod,
        bank_account: bankAccount, bank_ifsc: bankIfsc, bank_holder: bankHolder,
      });
      const fresh = await api.get(`/orders/${order.id}`);
      setOrder(fresh.data);
      setReturnOpen(false);
      setRetReason(""); setRetNotes(""); setRetCondition(""); setRefundMethod("original");
      setBankAccount(""); setBankIfsc(""); setBankHolder("");
      toast.success("Return request submitted · we'll email you within 24 hrs");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit return");
    } finally {
      setSubmitting(false);
    }
  };

  const returnStatus = order?.return_request?.status;
  const returnIdx = RETURN_STATUS_TIMELINE.findIndex((s) => s.key === returnStatus);

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12 py-12" data-testid="order-detail-page">
      <div className={`bg-white rounded-3xl p-8 border ${isCancelled ? "border-deepbrown/10 ring-2 ring-deepbrown/10" : "border-deepbrown/10"} text-center relative`}>
        <div className={`h-16 w-16 rounded-full grid place-items-center mx-auto ${isCancelled ? "bg-deepbrown/80" : "bg-emerald"}`}>
          {isCancelled ? <X className="h-8 w-8 text-white" /> : <CheckCircle2 className="h-8 w-8 text-white" />}
        </div>
        <h1 className="font-display text-3xl sm:text-4xl text-deepbrown font-bold mt-4">
          {isCancelled ? "Order cancelled" : (order.payment_status === "paid" ? "Payment received!" : "Order placed!")}
        </h1>
        <p className="text-deepbrown/60 mt-2">Order #{order.order_number}</p>

        {/* Customer cancel CTA */}
        {!isCancelled && cancellable.allowed && (
          <div className="mt-6 inline-flex flex-col items-center gap-2">
            <button
              onClick={() => setCancelOpen(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-deepbrown border-2 border-deepbrown/30 hover:border-rani hover:text-rani text-sm font-bold transition-colors"
              data-testid="customer-cancel-btn"
            >
              <X className="h-4 w-4" /> Cancel this order
            </button>
            <p className="text-[11px] uppercase tracking-widest text-marigold font-bold flex items-center gap-1">
              <Clock className="h-3 w-3" /> {cancellable.minutesLeft} min left to cancel
            </p>
          </div>
        )}
        {!isCancelled && !cancellable.allowed && order.status !== "shipped" && order.status !== "delivered" && (
          <div className="mt-6 flex flex-col items-center gap-3">
            <button
              disabled
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-deepbrown/10 text-deepbrown/40 border-2 border-deepbrown/10 text-sm font-bold cursor-not-allowed"
              data-testid="customer-cancel-btn-disabled"
              title="60-minute cancellation window has ended"
            >
              <X className="h-4 w-4" /> Cancel this order
            </button>
            <div className="max-w-lg mx-auto bg-marigold/15 border border-marigold/30 rounded-2xl p-4 text-left">
              <p className="flex items-start gap-2 text-sm text-deepbrown leading-relaxed">
                <Info className="h-4 w-4 text-rani mt-0.5 shrink-0" />
                <span>
                  <b>The 60-minute cancellation window has ended.</b> This order must be sent back as a <b>Return</b>.
                  Once we receive it, our team will perform a quality check and the refund will be processed within
                  5–7 business days of clearing QC. You&apos;ll be able to request a return as soon as the order ships.
                </span>
              </p>
            </div>
          </div>
        )}

        {/* Return CTA — only when shipped/delivered and within window and no return yet */}
        {!isCancelled && (order.status === "shipped" || order.status === "delivered") && !order.return_request && retEligible && (
          <div className="mt-6 flex flex-col items-center gap-2">
            {retEligible.eligible ? (
              <>
                <button
                  onClick={() => setReturnOpen(true)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-deepbrown border-2 border-deepbrown/30 hover:border-rani hover:text-rani text-sm font-bold transition-colors"
                  data-testid="return-request-btn"
                >
                  <Undo2 className="h-4 w-4" /> Request a return
                </button>
                <p className="text-[11px] uppercase tracking-widest text-deepbrown/50 font-bold">
                  Within {retEligible.window_days}-day return window
                </p>
              </>
            ) : (
              <div className="max-w-lg mx-auto bg-deepbrown/5 border border-deepbrown/10 rounded-xl p-3 text-xs text-deepbrown/70 text-center">
                {retEligible.message}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Cancellation banner */}
      {isCancelled && order.cancellation && (
        <div className="bg-deepbrown/5 border border-deepbrown/10 rounded-2xl p-5 mt-6" data-testid="cancellation-banner">
          <div className="flex gap-3 items-start">
            <AlertTriangle className="h-5 w-5 text-deepbrown shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-bold text-deepbrown">This order was cancelled by {order.cancellation.cancelled_by === "customer" ? "you" : "the Sutrakosh team"}.</p>
              <p className="text-deepbrown/80 mt-1"><b>Reason:</b> {REASON_LABELS[order.cancellation.reason] || order.cancellation.reason}</p>
              {order.cancellation.notes && (
                <p className="text-deepbrown/80 mt-1"><b>Notes:</b> {order.cancellation.notes}</p>
              )}
              <p className="text-[11px] text-deepbrown/50 mt-2">
                Cancelled on {new Date(order.cancellation.cancelled_at).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
              </p>
              {order.payment_status === "paid" && (
                <p className="mt-3 bg-emerald/10 text-emerald rounded-lg p-2 text-xs">
                  <b>Refund:</b> ₹{order.total.toLocaleString("en-IN")} will be refunded to your original payment method within 5–7 business days.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Progress */}
      <div className="bg-white rounded-2xl p-6 mt-6 border border-deepbrown/10">
        <h3 className="font-display text-xl text-deepbrown mb-6">Order Status</h3>
        <div className="flex items-center justify-between">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const active = i <= stepIdx;
            return (
              <div key={s.key} className="flex-1 flex flex-col items-center relative">
                <div className={`h-12 w-12 rounded-full grid place-items-center ${active ? "bg-rani text-white" : "bg-deepbrown/10 text-deepbrown/40"}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <p className={`text-xs font-semibold mt-2 ${active ? "text-deepbrown" : "text-deepbrown/40"}`}>{s.label}</p>
                {i < STEPS.length - 1 && (
                  <div className={`absolute top-6 left-1/2 w-full h-0.5 ${i < stepIdx ? "bg-rani" : "bg-deepbrown/10"}`} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Items */}
      <div className="bg-white rounded-2xl p-6 mt-6 border border-deepbrown/10">
        <h3 className="font-display text-xl text-deepbrown mb-4">Items</h3>
        <div className="space-y-3">
          {order.items.map((it, i) => (
            <div key={`${it.product_id}-${it.color || ''}-${it.size || ''}-${i}`} className="flex gap-4 items-center">
              <img src={it.image} className="h-16 w-16 rounded-lg object-cover" alt="" />
              <div className="flex-1">
                <p className="font-semibold text-deepbrown">{it.name}</p>
                <p className="text-xs text-deepbrown/60">
                  {it.color && `${it.color} · `}{it.size && `${it.size} · `}Qty {it.quantity}
                </p>
              </div>
              <p className="font-bold text-deepbrown">₹{(it.price * it.quantity).toLocaleString("en-IN")}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Summary + Address */}
      <div className="grid md:grid-cols-2 gap-6 mt-6">
        <div className="bg-white rounded-2xl p-6 border border-deepbrown/10">
          <h3 className="font-display text-lg text-deepbrown mb-3">Delivery Address</h3>
          <p className="text-sm leading-relaxed">
            <b>{order.address.full_name}</b><br />
            {order.address.line1}, {order.address.line2 && `${order.address.line2}, `}<br />
            {order.address.city}, {order.address.state} {order.address.pincode}<br />
            <span className="text-deepbrown/60">Phone: {order.address.phone}</span>
          </p>
        </div>
        <div className="bg-white rounded-2xl p-6 border border-deepbrown/10">
          <h3 className="font-display text-lg text-deepbrown mb-3">Payment</h3>
          <div className="text-sm space-y-1">
            <div className="flex justify-between"><span>Subtotal</span><span>₹{order.subtotal.toLocaleString("en-IN")}</span></div>
            <div className="flex justify-between"><span>Shipping</span><span>{order.shipping === 0 ? "FREE" : `₹${order.shipping}`}</span></div>
            {order.discount > 0 && (
              <div className="flex justify-between text-emerald"><span>Discount{order.coupon ? ` (${order.coupon.code})` : ""}</span><span>−₹{order.discount.toLocaleString("en-IN")}</span></div>
            )}
            <div className="flex justify-between font-display text-lg pt-2 border-t border-deepbrown/10 mt-2">
              <span>Total</span><span className="text-rani font-bold">₹{order.total.toLocaleString("en-IN")}</span>
            </div>
            <p className="text-xs text-deepbrown/60 mt-3 capitalize">
              Method: <b>{order.payment_method === "cod" ? "Cash on Delivery" : "Razorpay"}</b> · Status: <b className={order.payment_status === "paid" ? "text-emerald" : "text-marigold"}>{order.payment_status}</b>
            </p>
            {order.tracking_id && (
              <div className="mt-3 p-3 bg-marigold/15 rounded-xl">
                <p className="text-xs uppercase tracking-widest font-bold text-deepbrown">Tracking</p>
                <p className="text-sm font-bold text-deepbrown mt-1">{order.carrier || "Courier"}: {order.tracking_id}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Return status banner */}
      {order.return_request && (
        <div className="bg-white rounded-2xl p-6 border border-rani/20 mt-6" data-testid="return-status-banner">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-display text-xl text-deepbrown flex items-center gap-2"><Undo2 className="h-5 w-5 text-rani" /> Return in progress</h3>
            <span className="text-xs uppercase tracking-widest font-bold bg-rani/10 text-rani px-3 py-1 rounded-full">{order.return_request.status.replace(/_/g, " ")}</span>
          </div>
          {/* Timeline */}
          <div className="flex items-center justify-between mt-5 overflow-x-auto">
            {RETURN_STATUS_TIMELINE.map((s, i) => (
              <div key={s.key} className="flex-1 min-w-[80px] flex flex-col items-center relative">
                <div className={`h-8 w-8 rounded-full grid place-items-center text-[10px] font-bold ${i <= returnIdx ? "bg-rani text-white" : "bg-deepbrown/10 text-deepbrown/40"}`}>{i + 1}</div>
                <p className={`text-[10px] uppercase tracking-widest mt-1 text-center ${i <= returnIdx ? "text-deepbrown font-bold" : "text-deepbrown/40"}`}>{s.label}</p>
                {i < RETURN_STATUS_TIMELINE.length - 1 && (
                  <div className={`absolute top-4 left-1/2 w-full h-0.5 ${i < returnIdx ? "bg-rani" : "bg-deepbrown/10"}`} />
                )}
              </div>
            ))}
          </div>
          <div className="mt-5 grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-[10px] uppercase tracking-widest font-bold text-deepbrown/50">Reason</p>
              <p className="text-deepbrown">{RETURN_REASONS.find((r) => r.v === order.return_request.reason)?.l || order.return_request.reason}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest font-bold text-deepbrown/50">Refund</p>
              <p className="text-deepbrown">₹{(order.return_request.refund_amount || order.total).toLocaleString("en-IN")} · {order.return_request.refund_method === "bank_transfer" ? "Bank transfer" : "Original payment method"}</p>
            </div>
          </div>
          {order.return_request.status === "rejected" && order.return_request.rejection_reason && (
            <p className="mt-4 bg-deepbrown/10 text-deepbrown rounded-lg p-3 text-xs">
              <b>Rejection reason:</b> {order.return_request.rejection_reason}
            </p>
          )}
        </div>
      )}

      <div className="text-center mt-8">
        <Link to="/shop" className="inline-block px-7 py-3 rounded-full bg-deepbrown text-ivory font-bold hover:bg-rani">Continue Shopping</Link>
      </div>

      {/* Customer cancel dialog */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="max-w-md" data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Cancel this order?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-deepbrown/70">
              You can cancel free of charge within the first <b>{CUSTOMER_WINDOW_MIN} minutes</b> of placing the order.
              {cancellable.allowed && <> You have <b>{cancellable.minutesLeft} min</b> left.</>}
            </p>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-deepbrown/70">Reason</label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger data-testid="cancel-reason"><SelectValue placeholder="Pick a reason" /></SelectTrigger>
                <SelectContent>
                  {CANCEL_REASONS.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-deepbrown/70">Additional notes (optional)</label>
              <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything else you'd like us to know…" data-testid="cancel-notes" />
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={() => setCancelOpen(false)} className="flex-1 py-2.5 rounded-full bg-white border-2 border-deepbrown/20 text-deepbrown text-sm font-bold hover:border-deepbrown">
                Keep Order
              </button>
              <button onClick={submitCancel} disabled={submitting} className="flex-1 py-2.5 rounded-full bg-rani text-white text-sm font-bold disabled:opacity-50" data-testid="cancel-confirm">
                {submitting ? "Cancelling…" : "Cancel Order"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Return Request Dialog */}
      <Dialog open={returnOpen} onOpenChange={setReturnOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="return-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Undo2 className="h-5 w-5 text-rani" /> Request a return
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-deepbrown/70">
              Once submitted, our team reviews within 24 hours. After approval, a courier picks up the parcel, we quality-check at our workshop, and refund is initiated. Total timeline: <b>5–10 business days</b>.
            </p>
            <div>
              <Label>Reason for return *</Label>
              <Select value={retReason} onValueChange={setRetReason}>
                <SelectTrigger data-testid="return-reason"><SelectValue placeholder="Pick a reason" /></SelectTrigger>
                <SelectContent>
                  {RETURN_REASONS.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Item condition *</Label>
              <Select value={retCondition} onValueChange={setRetCondition}>
                <SelectTrigger data-testid="return-condition"><SelectValue placeholder="How is the item right now?" /></SelectTrigger>
                <SelectContent>
                  {RETURN_CONDITIONS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Additional details (optional)</Label>
              <Textarea rows={3} value={retNotes} onChange={(e) => setRetNotes(e.target.value)} placeholder="Photos help! You can also email images to our support after submitting." data-testid="return-notes" />
            </div>
            <div>
              <Label>Refund to *</Label>
              <Select value={refundMethod} onValueChange={setRefundMethod}>
                <SelectTrigger data-testid="refund-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {order.payment_method !== "cod" && <SelectItem value="original">Original payment method (Razorpay)</SelectItem>}
                  <SelectItem value="bank_transfer">Bank transfer (NEFT/IMPS)</SelectItem>
                </SelectContent>
              </Select>
              {order.payment_method === "cod" && (
                <p className="text-[11px] text-marigold mt-1">COD orders: refund is sent via bank transfer.</p>              )}
            </div>
            {refundMethod === "bank_transfer" && (
              <div className="bg-marigold/10 rounded-xl p-3 space-y-2">
                <p className="text-xs font-bold text-deepbrown">Bank details for refund</p>
                <Input placeholder="Account holder name" value={bankHolder} onChange={(e) => setBankHolder(e.target.value)} data-testid="bank-holder" />
                <Input placeholder="Account number" value={bankAccount} onChange={(e) => setBankAccount(e.target.value)} data-testid="bank-account" />
                <Input placeholder="IFSC code" value={bankIfsc} onChange={(e) => setBankIfsc(e.target.value.toUpperCase())} data-testid="bank-ifsc" />
                <p className="text-[10px] text-deepbrown/60">We use these only to credit your refund. Not stored beyond this order.</p>
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <button onClick={() => setReturnOpen(false)} className="flex-1 py-2.5 rounded-full bg-white border-2 border-deepbrown/20 text-deepbrown text-sm font-bold">
                Cancel
              </button>
              <button onClick={submitReturn} disabled={submitting} className="flex-1 py-2.5 rounded-full bg-rani text-white text-sm font-bold disabled:opacity-50" data-testid="return-submit">
                {submitting ? "Submitting…" : "Submit Return Request"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
